// Pre-forecast chat classifier — the "no forecast open yet" counterpart of
// classifyChatIntent. It runs from `answerChat` (App.jsx), the handler the routing ladder
// already delegates to when nothing is active and the message isn't an obvious forecast. So
// this adds intent-awareness to the pre-forecast state WITHOUT touching `handleChatMessage`
// (where the multiway routing lives): the ladder is unchanged; only what `answerChat` DOES
// changes.
//
// One LLM call that returns {intent, params, response}. It can (a) route a forecast-shaped
// message the regexes missed into a real forecast — resolving referents from the transcript
// ("what would THAT price be in a month" after discussing Bitcoin), (b) drive a UI panel
// ("file a bug", "open settings"), or (c) just answer (`explain`, with the reply in
// `response`). Same single round-trip the old plain-answer prompt used, so no added latency.
import { callLlm } from "../lib/forecast/llmClient.js";
import { todayAnchor } from "../lib/util/todayAnchor.js";
import { webSearchClause } from "../lib/forecast/webSearchClause.js";
import { earlierConversationClause } from "../lib/chat/chatMemory.js";
import { howSayerWorksClause } from "../lib/onboarding/tipEngine.js";

/** The pre-forecast classifier's parsed decision. */
export interface PreforecastIntent {
  intent: string;
  params?: Record<string, any>;
  response?: string;
}

/** Parsed decision (null if the reply wasn't valid JSON) plus the raw text. */
export interface PreforecastResult {
  parsed: PreforecastIntent | null;
  raw: string;
}

export interface ClassifyPreforecastArgs {
  question: string;
  /** Recent transcript ("User:"/"Sayer:" lines) for referent resolution. */
  memory?: string;
  /** The chat about other questions, grouped by question; "" when there is none. */
  earlier?: string;
  signal?: AbortSignal | null;
  /** The chat turn this answer belongs to (lib/chatTurn.js) — timing only. */
  turnId?: string | null;
  /**
   * Whether the question-type router is available. When true, a multi-outcome question
   * ("how many / which / what will X be") is forecastable and routes to `new_forecast`. When
   * false, only yes/no questions are forecastable — a multi-outcome question is NOT routed to
   * `new_forecast`; the classifier asks the user to rephrase as a yes/no with a date. Defaults
   * true.
   */
  autoQuestionTypeEnabled?: boolean;
  /**
   * Whether this request actually carries the web-search plugin. Threaded from the same
   * setting `callLlm({webSearch})` reads, so the prompt can never offer a tool the request
   * withheld. Defaults true (the historical behaviour).
   */
  webSearch?: boolean;
  /** How-to text for questions about using the app (onboarding/tips.js); "" when off. */
  howSayerWorks?: string;
  /** Injectable for tests; defaults to the real /api/forecast/llm passthrough. */
  llm?: (
    prompt: string,
    opts?: { signal?: AbortSignal | null; webSearch?: boolean; turnId?: string | null },
  ) => Promise<string>;
}

/**
 * Classify a message sent while NO forecast is open. Returns `{ parsed, raw }`; `parsed` is
 * the decision when the model emitted valid JSON, else null (caller falls back to `raw`).
 * Throws on LLM/transport error (incl. AbortError) — the caller owns abort/error handling.
 */
export async function classifyPreforecastIntent({
  question,
  memory = "",
  earlier = "",
  signal = null,
  turnId = null,
  autoQuestionTypeEnabled = true,
  webSearch = true,
  howSayerWorks = "",
  llm = callLlm,
}: ClassifyPreforecastArgs): Promise<PreforecastResult> {
  // Router on: multi-outcome questions are forecastable. Router off: only yes/no is — a
  // multi-outcome question is not routed to new_forecast; the user is asked to rephrase as a
  // yes/no with a date.
  const newForecastRule = autoQuestionTypeEnabled
    ? `- new_forecast: params {question} — the user is asking about a future or uncertain outcome (including "what will X be…", "how many…", "which…", "will X happen by…"). Resolve pronouns/referents from RECENT CONVERSATION into a FULLY-RESOLVED, self-contained question. Example: after discussing Bitcoin, "what would that price be in a month?" → params.question "What will the price of Bitcoin be one month from now?". Preserve any implicit-but-knowable timeframe; do NOT invent a specific date if none is knowable.`
    : `- new_forecast: params {question} — the user is asking a YES/NO question about a future outcome with a knowable resolution date ("will X happen by…?"). ONLY yes/no questions are forecastable here. A multi-outcome or numeric question — "how many…", "which…", "what will X be…" — is NOT forecastable: do NOT route it to new_forecast; use "explain" and ask the user to rephrase it as a yes/no question with a clear resolution date. Resolve pronouns/referents from RECENT CONVERSATION into a FULLY-RESOLVED, self-contained yes/no question; preserve any implicit-but-knowable timeframe, do NOT invent a specific date if none is knowable.`;
  const explainInvite = autoQuestionTypeEnabled
    ? `If they seem to want a forecast but it's not yet a clear question, invite them to ask a yes/no (or how-many/which) question with a clear timeframe.`
    : `If they seem to want a forecast but it's not yet a clear question, invite them to ask a YES/NO question with a clear resolution date (multi-outcome "how many / which / what will X be" questions aren't supported here — suggest a yes/no framing instead).`;
  const prompt = `You are Sayer, an AI forecasting assistant. No forecast is open yet — the user is chatting with you.

${todayAnchor()}

${earlierConversationClause(earlier)}RECENT CONVERSATION:
${memory || "(none)"}

${howSayerWorksClause(howSayerWorks)}Decide what the user wants and pick ONE intent:
${newForecastRule}
- file_bug: no params — the user wants to report a bug / says something is broken.
- open_settings: no params — the user wants settings (theme, engine, preferences).
- open_account: no params — the user wants their account / to log out.
- open_forecast_manager: no params — the user wants to see their past/saved forecasts.
- open_series_panel: no params — the user wants the time-series / indices panel.
- close_panels: no params — the user wants to dismiss/close open panels.
- explain: no params — a general or how-does-this-work question, OR you are unsure. Put your 1-4 sentence conversational answer in "response". ${explainInvite}

${webSearchClause(webSearch)}

Rules:
- Prefer "explain" when unsure; only use new_forecast when it is clearly a forecastable question.
- ALWAYS resolve "that / it / those / the price / the one I mentioned" against RECENT CONVERSATION before filling params.
- Never invent forecast results or numbers, and never state a probability of your own — running a forecast is what produces one. For "explain", "response" is your answer text.

USER MESSAGE: "${question}"

Reply with ONLY valid JSON (no markdown, no backticks):
{"intent":"...","params":{...},"response":"your conversational reply"}`;

  const text = await llm(prompt, { signal, webSearch, turnId });
  let parsed: PreforecastIntent | null;
  try {
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    parsed = jsonMatch ? JSON.parse(jsonMatch[0]) : null;
  } catch (_) {
    parsed = null;
  }
  return { parsed, raw: text };
}
